import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LOCALE } from "./core/config";

import enGBCommon from "../locales/en-GB/common.json";
import enGBErrors from "../locales/en-GB/errors.json";
import enGBDiary from "../locales/en-GB/diary.json";
import enGBOnboarding from "../locales/en-GB/onboarding.json";
import enGBTemplates from "../locales/en-GB/templates.json";

import deDECommon from "../locales/de-DE/common.json";
import deDEErrors from "../locales/de-DE/errors.json";
import deDEDiary from "../locales/de-DE/diary.json";
import deDEOnboarding from "../locales/de-DE/onboarding.json";
import deDETemplates from "../locales/de-DE/templates.json";

import daDKCommon from "../locales/da-DK/common.json";
import daDKErrors from "../locales/da-DK/errors.json";
import daDKDiary from "../locales/da-DK/diary.json";
import daDKOnboarding from "../locales/da-DK/onboarding.json";
import daDKTemplates from "../locales/da-DK/templates.json";

import enUSCommon from "../locales/en-US/common.json";
import enUSErrors from "../locales/en-US/errors.json";
import enUSDiary from "../locales/en-US/diary.json";
import enUSOnboarding from "../locales/en-US/onboarding.json";
import enUSTemplates from "../locales/en-US/templates.json";

import frFRCommon from "../locales/fr-FR/common.json";
import frFRErrors from "../locales/fr-FR/errors.json";
import frFRDiary from "../locales/fr-FR/diary.json";
import frFROnboarding from "../locales/fr-FR/onboarding.json";
import frFRTemplates from "../locales/fr-FR/templates.json";

import itITCommon from "../locales/it-IT/common.json";
import itITErrors from "../locales/it-IT/errors.json";
import itITDiary from "../locales/it-IT/diary.json";
import itITOnboarding from "../locales/it-IT/onboarding.json";
import itITTemplates from "../locales/it-IT/templates.json";

import esESCommon from "../locales/es-ES/common.json";
import esESErrors from "../locales/es-ES/errors.json";
import esESDiary from "../locales/es-ES/diary.json";
import esESOnboarding from "../locales/es-ES/onboarding.json";
import esESTemplates from "../locales/es-ES/templates.json";

export const i18nInstance = i18n.use(initReactI18next);

let initialised = false;

/** Initialise i18next once (client-side). Safe to call multiple times. */
export async function initI18n(language: string = DEFAULT_LOCALE): Promise<typeof i18n> {
    if (initialised) {
        if (i18n.language !== language) {
            await i18n.changeLanguage(language);
        }
        return i18n;
    }

    await i18nInstance.init({
        lng: language,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: ["en-GB", "en-US", "de-DE", "da-DK", "fr-FR", "it-IT", "es-ES"],
        nonExplicitSupportedLngs: true,
        load: "currentOnly",
        interpolation: { escapeValue: false },
        react: {
            useSuspense: false,
            bindI18n: "languageChanged loaded",
            bindI18nStore: "added removed",
        },
        defaultNS: "common",
        ns: ["common", "errors", "diary", "onboarding", "templates"],
        resources: {
            "en-GB": {
                common: enGBCommon,
                errors: enGBErrors,
                diary: enGBDiary,
                onboarding: enGBOnboarding,
                templates: enGBTemplates,
            },
            "de-DE": {
                common: deDECommon,
                errors: deDEErrors,
                diary: deDEDiary,
                onboarding: deDEOnboarding,
                templates: deDETemplates,
            },
            "da-DK": {
                common: daDKCommon,
                errors: daDKErrors,
                diary: daDKDiary,
                onboarding: daDKOnboarding,
                templates: daDKTemplates,
            },
            "en-US": {
                common: enUSCommon,
                errors: enUSErrors,
                diary: enUSDiary,
                onboarding: enUSOnboarding,
                templates: enUSTemplates,
            },
            "fr-FR": {
                common: frFRCommon,
                errors: frFRErrors,
                diary: frFRDiary,
                onboarding: frFROnboarding,
                templates: frFRTemplates,
            },
            "it-IT": {
                common: itITCommon,
                errors: itITErrors,
                diary: itITDiary,
                onboarding: itITOnboarding,
                templates: itITTemplates,
            },
            "es-ES": {
                common: esESCommon,
                errors: esESErrors,
                diary: esESDiary,
                onboarding: esESOnboarding,
                templates: esESTemplates,
            },
        },
    });

    initialised = true;
    return i18n;
}

export async function changeAppLanguage(language: string): Promise<void> {
    await initI18n(language);
    await i18n.changeLanguage(language);
}

export { useTranslation, Trans, I18nextProvider } from "react-i18next";
